import "../TransactionPanelShell.css";

import { Dispatch, FormEvent, ReactNode, SetStateAction } from "react";

import { CollaboratorTypeConstants } from "../../../../constants";
import { PillData, ProjectIssuanceTransactionPanelData, SelectData } from "../../../../models";
import { ServiceError } from "../../../../service/Shared";
import { formatNumber } from "../../../../utils";
import {
  ActionButtons,
  Column,
  DocumentListDocument,
  DocumentListVariant,
  ErrorBox,
  FlyoutPanel,
  FormLabel,
  InformationBox,
  PillsList,
  ReadonlyText,
  Row,
  Select,
  TextInput,
} from "../../../../widget";
import { isUnitKmHabitatType } from "../../../IssuancePanel";
import { TransactionAttachments } from "../TransactionAttachments";
import { TransactionCompanyPicker } from "../TransactionCompanyPicker";

export interface ProgressTransactionPanelProps {
  show: boolean;
  panelData: ProjectIssuanceTransactionPanelData;
  setPanelData: Dispatch<SetStateAction<ProjectIssuanceTransactionPanelData>>;
  typeData: SelectData;
  isBiodiversity?: boolean;
  habitatType?: string | null;
  variant?: DocumentListVariant;
  tagPills: PillData[];
  documents: DocumentListDocument[];
  settlementWarning?: string | null;
  settlementWarningTitle?: string | null;
  isFormLocked?: boolean;
  /*
    No permission to manage the project's issuances, so nothing here may be changed. Distinct from
    isFormLocked, which withholds only the fields that cannot resolve an over-commitment.
  */
  isReadOnly?: boolean;
  progressionNotice?: string | null;
  errors?: ServiceError[];
  attachmentsReadOnly?: boolean;
  isSubmitLoading?: boolean;
  overlayChildren?: ReactNode;
  onAddTag: () => void;
  onAddAttachment: () => void;
  onRemoveAttachment: (documentUuid: string) => void;
  onDownloadAttachment?: (documentUuid: string) => void;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
}

export const ProgressTransactionPanel = ({
  show,
  panelData,
  setPanelData,
  typeData,
  isBiodiversity = false,
  habitatType,
  variant = "seed",
  tagPills,
  documents,
  settlementWarning,
  settlementWarningTitle,
  isFormLocked = false,
  isReadOnly = false,
  progressionNotice,
  errors,
  attachmentsReadOnly = false,
  isSubmitLoading = false,
  overlayChildren,
  onAddTag,
  onAddAttachment,
  onRemoveAttachment,
  onDownloadAttachment,
  onSubmit,
  onClose,
}: ProgressTransactionPanelProps): JSX.Element => {
  const corners = variant === "seed" ? "rounded" : "default";

  return (
    <FlyoutPanel
      show={show}
      onClose={onClose}
      size="ExtraSmall"
      height="Full"
      className="TransactionPanel_flyout"
      header="Progress transaction"
      stickyHeader={false}
      buttonsChildren={undefined}
      contentChildren={
        <>
          <form className="TransactionPanel_form" onSubmit={onSubmit}>
            {errors && errors.length > 0 && (
              <Row spacingV="ll">
                <Column span={12}>
                  <ErrorBox
                    title="Please solve the below issues before confirming"
                    text={errors.map((error) => error.message)}
                  />
                </Column>
              </Row>
            )}
            {settlementWarning && (
              <Row spacingV="ll">
                <Column span={12}>
                  <InformationBox
                    variant="warning"
                    corners={corners}
                    title={settlementWarningTitle ?? "This issuance can no longer be progressed"}
                  >
                    <p className="body1">{settlementWarning}</p>
                  </InformationBox>
                </Column>
              </Row>
            )}
            {!settlementWarning && progressionNotice && (
              <Row spacingV="ll">
                <Column span={12}>
                  <InformationBox variant="info" corners={corners} title="This can only be done once">
                    <p className="body1">{progressionNotice}</p>
                  </InformationBox>
                </Column>
              </Row>
            )}
            <Row spacingV="ll">
              <Column span={12}>
                <ReadonlyText
                  label="Previous transaction ID"
                  value={panelData.parentTransactionUuid}
                  noDataAvailable={!panelData.parentTransactionUuid}
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <ReadonlyText
                  label="Counterparty"
                  value={panelData.counterPartyName}
                  noDataAvailable={!panelData.counterPartyName}
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <TransactionCompanyPicker
                  label="Broker"
                  show={show}
                  selectedUuid={panelData.brokerCollaboratorUuid}
                  selectedName={panelData.brokerName}
                  collaboratorTypes={CollaboratorTypeConstants.BROKER_TYPES}
                  disabled={isFormLocked || isReadOnly}
                  variant={variant}
                  onChange={(company) =>
                    setPanelData((prev) => ({
                      ...prev,
                      brokerCollaboratorUuid: company?.uuid ?? null,
                      brokerName: company?.name ?? null,
                    }))
                  }
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <Select
                  label="Type"
                  corners={corners}
                  data={typeData}
                  value={panelData.type}
                  disabled={isReadOnly}
                  onChange={(value) => setPanelData((prev) => ({ ...prev, type: value }))}
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <TextInput
                  label="Quantity"
                  name="Quantity"
                  type="number"
                  value={panelData.quantity?.toString()}
                  disabled={isReadOnly}
                  minNumberValue={0}
                  maxNumberValue={999999.99}
                  onChange={(value) =>
                    setPanelData((prev) => ({
                      ...prev,
                      quantity: value === "" ? null : parseFloat(Number(value).toFixed(5)),
                      totalValue:
                        prev.price && value !== ""
                          ? parseFloat(Number(value).toFixed(5)) * prev.price - (prev.fees ?? 0)
                          : null,
                    }))
                  }
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <TextInput
                  label="Price"
                  name="Price"
                  type="number"
                  prefix="£"
                  value={panelData.price?.toString()}
                  disabled={isFormLocked || isReadOnly}
                  minNumberValue={0}
                  maxNumberValue={9999999999.99999}
                  onChange={(value) =>
                    setPanelData((prev) => ({
                      ...prev,
                      price: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                      totalValue:
                        prev.quantity && value !== ""
                          ? parseFloat(Number(value).toFixed(4)) * prev.quantity - (prev.fees ?? 0)
                          : null,
                    }))
                  }
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <TextInput
                  label="Fees"
                  name="Fees"
                  type="number"
                  prefix="£"
                  value={panelData.fees?.toString()}
                  disabled={isFormLocked || isReadOnly}
                  minNumberValue={0}
                  maxNumberValue={9999999999.99999}
                  onChange={(value) =>
                    setPanelData((prev) => ({
                      ...prev,
                      fees: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                      totalValue:
                        prev.price && prev.quantity
                          ? prev.price * prev.quantity - (value === "" ? 0 : parseFloat(Number(value).toFixed(4)))
                          : null,
                    }))
                  }
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <ReadonlyText
                  label="Total value"
                  value={panelData.totalValue !== null ? `£${formatNumber(panelData.totalValue)}` : undefined}
                  noDataAvailable={panelData.totalValue === null}
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <TextInput
                  label="Transaction reference"
                  name="Transaction reference"
                  value={panelData.externalReference}
                  disabled={isFormLocked || isReadOnly}
                  onChange={(value) => setPanelData((prev) => ({ ...prev, externalReference: value }))}
                />
              </Column>
            </Row>
            {isBiodiversity && (
              <Row spacingV="ll">
                <Column span={12}>
                  <TextInput
                    label={`Unit size (${isUnitKmHabitatType(habitatType ?? null) ? "Km" : "Ha"})`}
                    name="Unit size Ha / Km"
                    type="number"
                    value={panelData.areaHa?.toString()}
                    disabled={isFormLocked || isReadOnly}
                    minNumberValue={0}
                    onChange={(value) =>
                      setPanelData((prev) => ({ ...prev, areaHa: value === "" ? null : Number(value) }))
                    }
                  />
                </Column>
              </Row>
            )}
            <Row spacingV="ll">
              <Column span={12}>
                <FormLabel label="Tags" />
                <PillsList
                  pills={tagPills}
                  linkText="+ Add a tag"
                  readOnly={isFormLocked || isReadOnly}
                  linkOnClick={onAddTag}
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <TransactionAttachments
                  documents={documents}
                  variant={variant}
                  readOnly={isFormLocked || isReadOnly || attachmentsReadOnly}
                  onAddAttachment={onAddAttachment}
                  onRemove={onRemoveAttachment}
                  onDownload={onDownloadAttachment}
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <ActionButtons
                  submitButtonLabel="Confirm"
                  corners={corners}
                  /*
                    Deliberately never disabled. A missing type, tags still loading and a caller without the
                    grant all refuse the submit in handleSubmit and say so, which a dead button cannot.
                  */
                  isLoading={isSubmitLoading}
                  onCancel={onClose}
                />
              </Column>
            </Row>
          </form>
          {/* Outside the form: these overlays carry their own buttons, which would otherwise submit it. */}
          {overlayChildren}
        </>
      }
    />
  );
};
